import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useConfig } from "../../contexts/ConfigContext";
import { useOptions } from "../../contexts/OptionsContext";
import {
  ApiEndpoint,
  ICmTableDataMediaItem,
  ICmTableDataRelation,
  MinimalDataItem,
  RelationshipEditorItem,
  RelationshipEditorState
} from "../../types";
import { EditorItemTable } from "../editor/EditorItemTable";
import MultiSelectDialog from "../common/MultiSelectDialog";
import { CustomDataGridTooltip } from "../editor/EditorItemTable";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import useAuth from "../../hooks/useAuth";
import { canAddNewFromPicker, NON_RELATIONSHIP_TABLE_SLUGS } from "../../utilities/relationshipUtils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RelationshipEditorTableProps {
  /** Current working state of relationships for this anchor. */
  editorState: RelationshipEditorState;
  /** ID of the anchor row (used as the DataGrid row id). */
  anchorId: number;
  anchorName?: string;
  anchorDescription?: string;
  /**
   * tableName of the anchor's own endpoint — filtered out from displayed columns
   * so an entity doesn't relate to its own table.
   */
  anchorTableSlug: string;
  /** Show the media column. False on Editor page, true in RelationshipEditDialog. */
  showMediaColumn: boolean;
  /** Called whenever the user confirms a selection change for a table slug. */
  onTableSlugChange: (tableSlug: string, items: RelationshipEditorItem[]) => void;
  isLoading?: boolean;
  /**
   * Current selection of restriction ids — `restrictions` is a plain field on
   * the row/media item (not a relationship), PATCHed directly by the parent
   * (EntityForm) on submit.
   */
  restrictionIds: number[];
  /** Called immediately when the user saves a new restrictions selection. */
  onRestrictionsChange: (ids: number[]) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Builds a synthetic DataGrid row from the editorState so that MultiSelectDialog
 * can read its initial selection from `row.relationships.byTableSlug`.
 */
function buildSyntheticRow(anchorId: number, anchorName: string, anchorDescription: string, editorState: RelationshipEditorState) {
  const byTableSlug: Record<string, ICmTableDataRelation[]> = {};
  for (const [slug, items] of Object.entries(editorState)) {
    if (slug !== "media") {
      byTableSlug[slug] = items as ICmTableDataRelation[];
    }
  }

  const rels = {
    byTableSlug,
    media: (editorState["media"] ?? []) as ICmTableDataMediaItem[]
  };

  // Cast needed because EditorItemTable expects MediaItemDetailed; only the
  // fields DataGrid columns access (id, name, description, relationships, relations)
  // are meaningful here.
  return {
    id: anchorId,
    name: anchorName,
    description: anchorDescription,
    type: "",
    original_file: "",
    relationships: rels,
    relations: rels
  } as any;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RelationshipEditorTable({
  editorState,
  anchorId,
  anchorName = "",
  anchorDescription = "",
  anchorTableSlug,
  showMediaColumn,
  onTableSlugChange,
  isLoading = false,
  restrictionIds,
  onRestrictionsChange
}: RelationshipEditorTableProps) {
  const config = useConfig();
  const { options, loadOptionsForEndpoint } = useOptions();
  const { enqueueSnackbar } = useSnackbar();
  const { canManageRestrictions } = useAuth();
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Keep options in a ref so column closures always see the latest value
  // without needing to recreate columns on every options update.
  const optionsRef = useRef(options);

  // Captures EditorItemTable's internal handleAddNew so MultiSelectDialog
  // can show "+ Add New" — set via the customColumns callback (CM-FE160-33).
  const handleAddNewRef = useRef<((category: string, onSuccess?: (id: number) => void) => void) | null>(null);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // ── Options loading ─────────────────────────────────────────────────────────

  const endpointsToLoad = useMemo(
    () =>
      config.apiEndpoints
        .filter((ep) => ep.tableName !== anchorTableSlug)
        // "restrictions" is excluded from the relationship columns themselves
        // (NON_RELATIONSHIP_TABLE_SLUGS) but its options are still needed for the
        // Restrictions column's MultiSelectDialog — only when the user can manage restrictions.
        .filter((ep) => (ep.tableName === "restrictions" && canManageRestrictions) || !NON_RELATIONSHIP_TABLE_SLUGS.includes(ep.tableName))
        .filter((ep) => ep.dataSourceType !== "fulcrum") // CM-FE160-32: exclude Fulcrum endpoints
        .filter((ep) => showMediaColumn || ep.entityType !== "media")
        .map((ep) => ep.tableName),
    [config.apiEndpoints, anchorTableSlug, showMediaColumn, canManageRestrictions]
  );

  useEffect(() => {
    const allLoaded = endpointsToLoad.every((name) => options?.[name] && Array.isArray(options[name]));
    if (allLoaded) return;

    setIsLoadingOptions(true);
    loadOptionsForEndpoint(endpointsToLoad)
      .then(() => setIsLoadingOptions(false))
      .catch(() => {
        setIsLoadingOptions(false);
        enqueueUniqueSnackbar(enqueueSnackbar, "Failed to load relationship options", {
          variant: "error"
        });
      });
  }, [endpointsToLoad]);

  // ── Column generation ───────────────────────────────────────────────────────

  const buildRelationshipColumn = useCallback(
    (ep: ApiEndpoint): GridColDef => {
      const tableSlug = ep.tableName;

      const handleSaved = (ids: number[]) => {
        const currentItems = editorState[tableSlug] ?? [];

        const items: RelationshipEditorItem[] = ids.map((id) => {
          // Preserve existing item to keep relationship_id intact
          const existing = currentItems.find((item) => item.id === id);
          if (existing) return existing;

          // New selection — build a shell from options data
          const opt = optionsRef.current?.[tableSlug]?.find((o: MinimalDataItem) => o.id === id);
          if (ep.entityType === "media") {
            return {
              id,
              name: opt?.name ?? `ID: ${id}`,
              entity_description: "",
              rel_description: "",
              relationship_id: null as any,
              is_primary: false,
              media_type: "",
              url: "",
              thumbnail: ""
            } as ICmTableDataMediaItem;
          }
          return {
            id,
            name: opt?.name ?? `ID: ${id}`,
            entity_description: "",
            rel_description: "",
            relationship_id: null as any,
            is_primary: false,
            primary_image: null,
            thumbnail: null,
            table_slug: tableSlug,
            table_name: ep.name
          } as ICmTableDataRelation;
        });

        onTableSlugChange(tableSlug, items);
      };

      return {
        field: tableSlug,
        headerName: ep.name,
        flex: 1,
        maxWidth: 200,
        minWidth: 120,
        editable: true,
        sortable: false,
        valueGetter: () => {
          const items = editorState[tableSlug] ?? [];
          return items.map((i) => i.id);
        },
        renderCell: (params: GridRenderCellParams) => {
          const items = editorState[tableSlug] ?? [];
          if (items.length === 0) {
            return <Typography sx={{ color: "#98A2B3", fontSize: "0.8125rem", fontStyle: "italic" }}>Click to add</Typography>;
          }
          const displayNames = items.map((item) => {
            const opt = optionsRef.current?.[tableSlug]?.find((o: MinimalDataItem) => o.id === item.id);
            return opt?.name ?? item.name ?? `ID: ${item.id}`;
          });
          return (
            <CustomDataGridTooltip
              title={
                <Box sx={{ maxHeight: "20rem", overflowY: "auto", pr: "0.5rem" }}>
                  <Typography variant='body2' fontWeight={500} py='0.5rem'>
                    {ep.name} ({items.length})
                  </Typography>
                  {displayNames.map((name, idx) => (
                    <Typography key={idx} variant='caption' display='block'>
                      {name}
                    </Typography>
                  ))}
                </Box>
              }
              arrow={false}
            >
              <Typography
                variant='caption'
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  fontSize: "0.75rem",
                  display: "block"
                }}
              >
                {displayNames.join(", ")}
              </Typography>
            </CustomDataGridTooltip>
          );
        },
        renderEditCell: (params) => (
          <MultiSelectDialog
            params={params}
            options={optionsRef.current?.[tableSlug] ?? []}
            category={ep.name}
            config={config}
            onSaved={handleSaved}
            onAddNew={
              // CM-FE160-33: wire "+ Add New" from EditorItemTable's handleAddNew
              canAddNewFromPicker(ep) && handleAddNewRef.current ? (onSuccess) => handleAddNewRef.current!(ep.name, onSuccess) : undefined
            }
          />
        )
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorState, config, onTableSlugChange]
  );

  const columns: GridColDef[] = useMemo(() => {
    const nameCol: GridColDef = {
      field: "name",
      headerName: "Name",
      flex: 1,
      maxWidth: 260,
      minWidth: 160,
      editable: false,
      renderCell: (params) => (
        <Typography variant='body2' sx={{ fontWeight: "medium" }}>
          {params.value ?? ""}
        </Typography>
      )
    };

    const descCol: GridColDef = {
      field: "description",
      headerName: "Description",
      flex: 1,
      maxWidth: 300,
      minWidth: 200,
      editable: false,
      renderCell: (params) => (
        <Typography variant='caption' color='text.secondary'>
          {params.value ?? "No description"}
        </Typography>
      )
    };

    const relCols = config.apiEndpoints
      .filter((ep) => ep.tableName !== anchorTableSlug)
      .filter((ep) => !NON_RELATIONSHIP_TABLE_SLUGS.includes(ep.tableName))
      .filter((ep) => ep.dataSourceType !== "fulcrum") // CM-FE160-32: exclude Fulcrum endpoints
      .filter((ep) => showMediaColumn || ep.entityType !== "media")
      .map(buildRelationshipColumn);

    // "Restrictions" — a plain `restrictions: number[]` field on the row/media item
    // (not a relationship/anchor-relationships), edited here via the same
    // MultiSelectDialog used by the Editor grid's restrictions column.
    const restrictionsEndpoint = config.apiEndpoints.find((ep) => ep.tableName === "restrictions");
    const restrictionsCol: GridColDef = {
      field: "restrictions",
      headerName: "Restrictions",
      flex: 1,
      maxWidth: 200,
      minWidth: 120,
      editable: true,
      sortable: false,
      valueGetter: () => restrictionIds,
      renderCell: () => {
        if (restrictionIds.length === 0) {
          return <Typography sx={{ color: "#98A2B3", fontSize: "0.8125rem", fontStyle: "italic" }}>Click to add</Typography>;
        }
        const displayNames = restrictionIds.map((id) => {
          const opt = optionsRef.current?.["restrictions"]?.find((o: MinimalDataItem) => o.id === id);
          return opt?.name ?? `ID: ${id}`;
        });
        return (
          <Typography
            variant='caption'
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
              fontSize: "0.75rem",
              display: "block"
            }}
          >
            {displayNames.join(", ")}
          </Typography>
        );
      },
      renderEditCell: (params) => (
        <MultiSelectDialog
          params={params}
          options={optionsRef.current?.["restrictions"] ?? []}
          category={restrictionsEndpoint?.name ?? "Restrictions"}
          config={config}
          onSaved={(ids) => onRestrictionsChange(ids)}
          onAddNew={
            handleAddNewRef.current
              ? (onSuccess) => handleAddNewRef.current!(restrictionsEndpoint?.name ?? "Restrictions", onSuccess)
              : undefined
          }
        />
      )
    };

    return [nameCol, descCol, ...relCols, ...(canManageRestrictions && restrictionsEndpoint ? [restrictionsCol] : [])];
  }, [
    config.apiEndpoints,
    anchorTableSlug,
    showMediaColumn,
    buildRelationshipColumn,
    restrictionIds,
    onRestrictionsChange,
    canManageRestrictions
  ]);

  // ── Synthetic row ───────────────────────────────────────────────────────────

  const syntheticRow = useMemo(
    () => buildSyntheticRow(anchorId, anchorName, anchorDescription, editorState),
    [anchorId, anchorName, anchorDescription, editorState]
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  const loading = isLoading || isLoadingOptions;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {loading && !syntheticRow ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Loading relationship options…
          </Typography>
        </Box>
      ) : (
        <EditorItemTable
          customColumns={(handleAddNew) => {
            // CM-FE160-33: capture so MultiSelectDialog renderEditCell can use it
            handleAddNewRef.current = handleAddNew as any;
            return columns;
          }}
          items={[syntheticRow]}
          selectedItemId={anchorId}
          isLoading={loading}
          page={0}
          currentEndpoint={undefined}
          totalRowCounts={1}
          onItemUpdate={(newRow) => newRow}
          mode='edit'
          hideFooter
        />
      )}
    </Box>
  );
}
